/**
 * 章节范围验证 Hook
 *
 * 提供 Agent 节点的章节范围验证功能
 * - 从父级循环容器获取允许的章节范围
 * - 验证工具调用的章节参数是否在范围内
 * - 提供超出范围时的错误提示
 */

import { useMemo, useCallback } from 'react';
import { useWorkflowStore } from '../store/workflowStore';
import type { LoopNodeData, AgentNodeData, WorkflowNode } from '../types/workflow';

/** 章节范围 */
export interface ChapterRange {
  start: number;
  end: number;
}

/** 验证结果 */
export interface ChapterValidationResult {
  /** 是否有效 */
  isValid: boolean;
  /** 父级循环容器（如果有） */
  parentLoopNode: WorkflowNode | null;
  /** 允许的章节范围（如果有循环容器） */
  allowedRange: ChapterRange | null;
  /** 错误信息 */
  error: string | null;
}

/**
 * 查找节点的父级循环容器
 *
 * 1. 首先检查节点的 parentId 属性（subflows 方案）
 * 2. 如果没有，通过追溯边的连接关系找到循环容器
 */
function findParentLoopNode(
  nodeId: string,
  nodes: WorkflowNode[],
  edges: { source: string; target: string }[]
): WorkflowNode | null {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return null;

  // 方案1：通过 parentId 查找（subflows 方案）
  if (node.parentId) {
    const parentLoopNode = nodes.find((n) => n.id === node.parentId && n.type === 'loop');
    if (parentLoopNode) {
      return parentLoopNode;
    }
  }

  // 方案2：通过追溯边的连接关系查找（兼容旧方案）
  const visited = new Set<string>();
  const queue: string[] = [nodeId];

  while (queue.length > 0) {
    const currentId = queue.shift()!;

    if (visited.has(currentId)) {
      continue;
    }
    visited.add(currentId);

    const incomingEdges = edges.filter((e) => e.target === currentId);

    for (const edge of incomingEdges) {
      const sourceNode = nodes.find((n) => n.id === edge.source);

      if (!sourceNode) {
        continue;
      }

      if (sourceNode.type === 'loop') {
        return sourceNode;
      }

      queue.push(edge.source);
    }
  }

  return null;
}

/**
 * 获取循环容器的章节范围
 */
function getLoopChapterRange(loopNode: WorkflowNode): ChapterRange | null {
  const data = loopNode.data as LoopNodeData;
  if (
    typeof data.chapter_start === 'number' &&
    typeof data.chapter_end === 'number'
  ) {
    return {
      start: data.chapter_start,
      end: data.chapter_end,
    };
  }
  return null;
}

/**
 * 验证章节是否在范围内
 */
function validateChapterInRange(
  chapter: number,
  range: ChapterRange
): { isValid: boolean; error: string | null } {
  if (chapter < range.start || chapter > range.end) {
    return {
      isValid: false,
      error: `章节 ${chapter} 超出循环容器范围（${range.start}-${range.end}）`,
    };
  }
  return { isValid: true, error: null };
}

/**
 * 章节范围验证 Hook
 *
 * @param nodeId - 要验证的节点 ID（通常是 Agent 节点）
 * @returns 验证结果和相关工具函数
 */
export function useChapterRangeValidation(nodeId: string): ChapterValidationResult {
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);

  return useMemo(() => {
    // 查找父级循环容器
    const parentLoopNode = findParentLoopNode(nodeId, nodes, edges);

    // 如果没有父级循环容器，不需要验证
    if (!parentLoopNode) {
      return {
        isValid: true,
        parentLoopNode: null,
        allowedRange: null,
        error: null,
      };
    }

    // 获取允许的章节范围
    const allowedRange = getLoopChapterRange(parentLoopNode);

    if (!allowedRange) {
      return {
        isValid: true,
        parentLoopNode,
        allowedRange: null,
        error: null,
      };
    }

    // 基本验证通过（范围存在）
    return {
      isValid: true,
      parentLoopNode,
      allowedRange,
      error: null,
    };
  }, [nodeId, nodes, edges]);
}

/**
 * 验证章节参数 Hook
 *
 * 用于验证特定章节号是否在允许范围内
 */
export function useChapterParameterValidation(nodeId: string) {
  const validation = useChapterRangeValidation(nodeId);

  /**
   * 验证单个章节号
   */
  const validateChapter = useCallback(
    (chapter: number): { isValid: boolean; error: string | null } => {
      if (!validation.allowedRange) {
        // 没有循环容器限制，允许任意章节
        return { isValid: true, error: null };
      }

      return validateChapterInRange(chapter, validation.allowedRange);
    },
    [validation.allowedRange]
  );

  /**
   * 验证章节范围
   */
  const validateChapterRange = useCallback(
    (start: number, end: number): { isValid: boolean; errors: string[] } => {
      const errors: string[] = [];

      if (!validation.allowedRange) {
        // 没有循环容器限制，允许任意章节
        return { isValid: true, errors: [] };
      }

      // 验证起始章节
      const startResult = validateChapterInRange(start, validation.allowedRange);
      if (!startResult.isValid && startResult.error) {
        errors.push(startResult.error);
      }

      // 验证结束章节
      const endResult = validateChapterInRange(end, validation.allowedRange);
      if (!endResult.isValid && endResult.error) {
        errors.push(endResult.error);
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    },
    [validation.allowedRange]
  );

  /**
   * 批量验证多个章节
   */
  const validateChapters = useCallback(
    (chapters: number[]): { isValid: boolean; errors: string[] } => {
      const errors: string[] = [];

      if (!validation.allowedRange) {
        return { isValid: true, errors: [] };
      }

      for (const chapter of chapters) {
        const result = validateChapterInRange(chapter, validation.allowedRange);
        if (!result.isValid && result.error) {
          errors.push(result.error);
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    },
    [validation.allowedRange]
  );

  return {
    ...validation,
    validateChapter,
    validateChapterRange,
    validateChapters,
  };
}

/**
 * 获取所有 Agent 节点的章节范围验证结果
 *
 * 用于在执行前批量检查所有 Agent 节点
 */
export function useAllAgentChapterValidation(): Map<string, ChapterValidationResult> {
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);

  return useMemo(() => {
    const results = new Map<string, ChapterValidationResult>();

    // 找到所有 Agent 节点
    const agentNodes = nodes.filter((n) => n.type === 'agent');

    for (const agentNode of agentNodes) {
      const parentLoopNode = findParentLoopNode(agentNode.id, nodes, edges);
      const allowedRange = parentLoopNode
        ? getLoopChapterRange(parentLoopNode)
        : null;

      results.set(agentNode.id, {
        isValid: true, // 基础验证通过
        parentLoopNode,
        allowedRange,
        error: null,
      });
    }

    return results;
  }, [nodes, edges]);
}

/**
 * 检查工作流是否可以执行
 *
 * 检查所有在循环容器内的 Agent 节点是否配置正确
 */
export function useCanExecuteWorkflow(): {
  canExecute: boolean;
  errors: Array<{ nodeId: string; nodeName: string; error: string }>;
} {
  const nodes = useWorkflowStore((state) => state.nodes);
  const edges = useWorkflowStore((state) => state.edges);

  return useMemo(() => {
    const errors: Array<{ nodeId: string; nodeName: string; error: string }> = [];

    // 检查是否有开始节点
    const hasStartNode = nodes.some((n) => n.type === 'start');
    if (!hasStartNode) {
      errors.push({
        nodeId: '',
        nodeName: '工作流',
        error: '工作流缺少开始节点',
      });
    }

    // 检查所有 Agent 节点
    const agentNodes = nodes.filter((n) => n.type === 'agent');
    for (const agentNode of agentNodes) {
      const data = agentNode.data as AgentNodeData;

      // 检查是否选择了 Agent
      if (!data.agent_id) {
        errors.push({
          nodeId: agentNode.id,
          nodeName: data.label || 'Agent',
          error: '请选择一个 Agent',
        });
      }

      // 检查章节范围配置（如果有循环容器）
      const parentLoopNode = findParentLoopNode(agentNode.id, nodes, edges);
      if (parentLoopNode) {
        const loopData = parentLoopNode.data as LoopNodeData;
        // 如果节点配置了章节范围，验证是否在循环容器范围内
        if (data.chapter_range) {
          if (
            data.chapter_range.start < loopData.chapter_start ||
            data.chapter_range.end > loopData.chapter_end
          ) {
            errors.push({
              nodeId: agentNode.id,
              nodeName: data.label || 'Agent',
              error: `章节范围 (${data.chapter_range.start}-${data.chapter_range.end}) 超出循环容器范围 (${loopData.chapter_start}-${loopData.chapter_end})`,
            });
          }
        }
      }
    }

    return {
      canExecute: errors.length === 0,
      errors,
    };
  }, [nodes, edges]);
}

export default useChapterRangeValidation;
